import * as React from "react";
import { Control, useFieldArray, useWatch } from "react-hook-form";
import {
  Text,
  Grid,
  Title,
  Button,
  ActionIcon,
  Modal,
  Stack,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";

import {
  CSelect,
  CTextInput,
  CCheckbox,
  CMultiSelect,
  CTextarea,
} from "@supportinitiative/react-hook-form-mantine";

import { UseCaseCVMContext } from "../UseCaseCVMContext";
import {
  getDataset,
  getDatasetColumnsLOV,
  columnTypes,
  getColumnsLOV,
  getDatasetsLOV,
} from "../UseCaseUtils";
import { useDatasets } from "../../hooks";
import { UseCaseType } from "@supportinitiative/common";

function Miscellaneous({
  name,
  masterDataset,
  control,
}: {
  name: `definition.masterDataset`;
  masterDataset: string;
  control: Control<UseCaseType>;
}) {
  const { state: useCaseFormState } = React.useContext(UseCaseCVMContext);
  const { data: datasets } = useDatasets(useCaseFormState.entityId);

  const masterDatasetColumns = useWatch({
    control,
    name: `${name}.columns`,
  });

  const masterDatasetAdditionalColumns = useWatch({
    control,
    name: `${name}.additionalColumns`,
  });

  return (
    <>
      <Grid align="stretch" gutter={"xs"} mb="xs">
        <Grid.Col span={12}>
          <CMultiSelect
            name={`${name}.partitionBy`}
            control={control}
            searchable
            label="Parition By"
            data={getColumnsLOV(
              getDataset(datasets, masterDataset),
              masterDatasetColumns || [],
              masterDatasetAdditionalColumns || []
            )}
            disabled={useCaseFormState.actionOptions.disableAll}
          />
        </Grid.Col>
      </Grid>

      <Grid align="stretch" gutter={"xs"} mb="xs">
        <Grid.Col span={12}>
          <CTextarea
            name={`${name}.accessListOfValues`}
            control={control}
            label="Access Values"
            autosize
            minRows={2}
            maxRows={20}
            disabled={useCaseFormState.actionOptions.disableAll}
          />
        </Grid.Col>
      </Grid>
      {/* <Grid align="stretch" gutter={"xs"} mb="xs">
        <Grid.Col span={12}>
          <CTextInput
            name={`${name}.accessPolicies`}
            control={control}
            label="Access Policies"
            disabled={useCaseFormState.actionOptions.disableAll}
          />
        </Grid.Col>
      </Grid> */}
    </>
  );
}

function MasterDatasetCVM({
  name,
  control,
}: {
  name: `definition.masterDataset`;
  control: Control<UseCaseType>;
}) {
  const { state: useCaseFormState } = React.useContext(UseCaseCVMContext);
  let [isOpen, setIsOpen] = React.useState(false);
  const { data: datasets } = useDatasets(useCaseFormState.entityId);

  const additionalColumnsArray = useFieldArray({
    control,
    name: `${name}.additionalColumns`,
    keyName: "key",
  });

  const masterDataset = useWatch({
    control,
    name: `${name}.datasetId`,
  });

  const type = useWatch({
    control,
    name: `${name}.type`,
  });

  const masterDatasetName = useWatch({
    control,
    name: `${name}.name`,
  });

  const columns = useWatch({
    control,
    name: `${name}.columns`,
  });

  React.useEffect(() => {
    if (masterDataset) {
      let dataset = getDataset(datasets, masterDataset);
      if (dataset) {
        if (!columns || columns.length === 0) {
          // Only set columns when existing columns are empty
          let datasetColumns = (dataset.definition.columns || []).map((col) => {
            return col.name;
          });
          useCaseFormState.setValue(`${name}.columns`, datasetColumns);
        }
      }
    }
  }, [masterDataset]);

  return (
    <Stack>
      <Button
        fullWidth
        variant="default"
        // color="teal"
        onClick={() => {
          setIsOpen(!isOpen);
        }}
      >
        {masterDatasetName ? masterDatasetName : "Master Dataset"}
      </Button>

      <Modal
        centered
        size={"xl"}
        opened={isOpen}
        onClose={() => setIsOpen(!isOpen)}
        title={<Title order={3}>Master Dataset</Title>}
      >
        <Grid align="stretch" gutter={"xs"} mb="xs">
          <Grid.Col span={12}>
            <CSelect
              name={`${name}.type`}
              label="Type"
              control={control}
              data={[
                { label: "Dataset", value: "DATASET" },
                // { label: "Self", value: "SELF" }, // No need for now. This is anyway managed as to how data load happens
                { label: "None", value: "NONE" },
              ]}
              showErrorText={false}
              searchable
              clearable
              required
              disabled={useCaseFormState.actionOptions.disableAll}
            />
          </Grid.Col>
        </Grid>
        {type === "DATASET" && (
          <>
            <Grid align="stretch" gutter={"xs"} mb="xs">
              <Grid.Col span={6}>
                <CTextInput
                  name={`${name}.id`}
                  label="ID"
                  control={control}
                  required
                  disabled={true}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <CTextInput
                  name={`${name}.name`}
                  label="Name"
                  control={control}
                  required
                  disabled={useCaseFormState.actionOptions.disableAll}
                />
              </Grid.Col>
            </Grid>

            <Grid align="stretch" gutter={"xs"} mb="xs">
              <Grid.Col span={12}>
                <CSelect
                  name={`${name}.datasetId`}
                  label="Dataset"
                  control={control}
                  data={getDatasetsLOV(datasets)}
                  showErrorText={false}
                  searchable
                  clearable
                  required
                  disabled={useCaseFormState.actionOptions.disableAll}
                />
              </Grid.Col>
            </Grid>

            <Grid align="stretch" gutter={"xs"} mb="xs">
              <Grid.Col span={12}>
                <CMultiSelect
                  name={`${name}.columns`}
                  control={control}
                  searchable
                  required
                  label="Columns"
                  data={getDatasetColumnsLOV(
                    getDataset(datasets, masterDataset)
                  )}
                  disabled={useCaseFormState.actionOptions.disableAll}
                />
              </Grid.Col>
            </Grid>

            <div
              style={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Text fw={500} style={{ fontSize: "14px" }}>
                Additional Columns
              </Text>
              <ActionIcon
                size="sm"
                variant="white"
                color="gray"
                onClick={() => {
                  additionalColumnsArray.append({
                    name: "",
                    type: "TEXT",
                    label: "",
                    genFunction: "",
                    notNull: "NO",
                  });
                }}
                disabled={useCaseFormState.actionOptions.disableAll}
                style={{
                  display: useCaseFormState.actionOptions.disableAll
                    ? "none"
                    : "",
                }}
              >
                <IconPlus />
              </ActionIcon>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                marginBottom: "10px",
                width: "100%",
              }}
            >
              <Grid align="stretch" gutter={"xs"} mt="xs">
                <Grid.Col span={12}>
                  {additionalColumnsArray.fields.map((field, index) => {
                    return (
                      <Grid
                        align="stretch"
                        gutter={"xs"}
                        mb="xs"
                        key={field.key}
                      >
                        <Grid.Col span={2}>
                          <CTextInput
                            name={`${name}.additionalColumns.${index}.name`}
                            control={control}
                            showErrorText={false}
                            label={index === 0 ? "Name" : ""}
                            required
                            disabled={useCaseFormState.actionOptions.disableAll}
                          />
                        </Grid.Col>
                        <Grid.Col span={2}>
                          <CSelect
                            name={`${name}.additionalColumns.${index}.type`}
                            control={control}
                            label={index === 0 ? "Type" : ""}
                            data={columnTypes}
                            showErrorText={false}
                            required
                            disabled={useCaseFormState.actionOptions.disableAll}
                          />
                        </Grid.Col>
                        <Grid.Col span={2}>
                          <CTextInput
                            name={`${name}.additionalColumns.${index}.label`}
                            control={control}
                            showErrorText={false}
                            required
                            label={index === 0 ? "Label" : ""}
                            disabled={useCaseFormState.actionOptions.disableAll}
                          />
                        </Grid.Col>
                        <Grid.Col span={2}>
                          <CTextInput
                            name={`${name}.additionalColumns.${index}.genFunction`}
                            control={control}
                            showErrorText={false}
                            required
                            label={index === 0 ? "Gen Function" : ""}
                            disabled={useCaseFormState.actionOptions.disableAll}
                          />
                        </Grid.Col>
                        <Grid.Col
                          span={1}
                          style={{
                            display: "flex",
                            alignItems: "flex-end",
                          }}
                        >
                          <CTextInput
                            name={`${name}.additionalColumns.${index}.id`}
                            control={control}
                            label={index === 0 ? "Id" : ""}
                            disabled={true}
                          />
                        </Grid.Col>
                        <Grid.Col
                          span={2}
                          style={{
                            display: "flex",
                            alignItems: "flex-end",
                          }}
                        >
                          <CCheckbox
                            name={`${name}.additionalColumns.${index}.notNull`}
                            control={control}
                            label="Not Null"
                            trueValue="YES"
                            falseValue="NO"
                            disabled={useCaseFormState.actionOptions.disableAll}
                          />
                        </Grid.Col>
                        <Grid.Col
                          span={1}
                          style={{
                            display: "flex",
                            alignItems: "flex-end",
                          }}
                        >
                          <ActionIcon
                            size="sm"
                            variant="white"
                            color="gray"
                            onClick={() => {
                              additionalColumnsArray.remove(index);
                            }}
                            disabled={useCaseFormState.actionOptions.disableAll}
                            style={{
                              display: useCaseFormState.actionOptions.disableAll
                                ? "none"
                                : "",
                            }}
                          >
                            <IconTrash />
                          </ActionIcon>
                        </Grid.Col>
                      </Grid>
                    );
                  })}
                </Grid.Col>
              </Grid>
            </div>

            <Miscellaneous
              name={name}
              masterDataset={masterDataset}
              control={control}
            />
          </>
        )}
        <Grid align="stretch" gutter={"xs"} mt="xs">
          <Grid.Col span={2}>
            <Button
              color="teal"
              variant="filled"
              fullWidth
              onClick={() => setIsOpen(!isOpen)}
            >
              Ok
            </Button>
          </Grid.Col>
        </Grid>
      </Modal>
    </Stack>
  );
}

export default MasterDatasetCVM;
